/// Plain-text → safe-link rendering for the dev-blog board.
///
/// Bodies are stored and rendered as plain text — no HTML/Markdown
/// is ever interpreted, so there is no injection surface. The only
/// transformation is turning bare http(s) URLs into external links.
/// `linkifySegments` is the pure splitter (unit-tested); `Linkified`
/// renders the segments. Whitespace/newlines are preserved by the
/// caller wrapping `<Linkified>` in a `whitespace-pre-wrap` element.

import { Fragment } from 'react'

export type LinkSegment =
  | { kind: 'text'; value: string }
  | { kind: 'link'; value: string; href: string }

// http(s):// runs of non-whitespace, stopping before chars that are
// almost never part of a URL in prose.
const URL_RE = /https?:\/\/[^\s<>"')\]}]+/g
// Trailing punctuation that, when a URL is followed by it in prose,
// belongs to the sentence rather than the link.
const TRAILING_PUNCT_RE = /[.,!?;:)\]}]+$/

export function linkifySegments(text: string): LinkSegment[] {
  const segments: LinkSegment[] = []
  let lastIndex = 0
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0
    let url = m[0]
    let suffix = ''
    const trailing = url.match(TRAILING_PUNCT_RE)
    if (trailing) {
      suffix = trailing[0]
      url = url.slice(0, url.length - suffix.length)
    }
    if (start > lastIndex) segments.push({ kind: 'text', value: text.slice(lastIndex, start) })
    if (url.length > 0) segments.push({ kind: 'link', value: url, href: url })
    if (suffix.length > 0) segments.push({ kind: 'text', value: suffix })
    lastIndex = start + m[0].length
  }
  if (lastIndex < text.length) segments.push({ kind: 'text', value: text.slice(lastIndex) })
  if (segments.length === 0) segments.push({ kind: 'text', value: text })
  return segments
}

export function Linkified(props: { text: string }) {
  return (
    <>
      {linkifySegments(props.text).map((seg, i) =>
        seg.kind === 'link' ? (
          <a
            key={i}
            href={seg.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-emerald-300 underline decoration-emerald-400/40 underline-offset-2 hover:decoration-emerald-300"
          >
            {seg.value}
          </a>
        ) : (
          <Fragment key={i}>{seg.value}</Fragment>
        ),
      )}
    </>
  )
}
